import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAD_API = 'https://propuestas.mikel.media/api/lead';
const WHATSAPP_NUMBER = '17874222734';
const BOOKING_URL = 'https://bookme.name/professionalchameleon/consultation';

const COPY = {
  es: {
    eyebrow: 'Agencia de marketing digital · Puerto Rico',
    title: 'Anuncios. Contenido. Web. Automatización.',
    titleAccent: 'Un solo equipo.',
    sub: 'Cuéntanos de tu negocio y te respondemos por WhatsApp en menos de 24 horas con una propuesta clara.',
    bullets: ['Anuncios en Meta y Google', 'Contenido y redes sociales', 'Web, SEO y visibilidad en IA', 'Automatización y gestión de proyectos'],
    name: 'Tu nombre',
    business: 'Nombre de tu negocio',
    phone: 'WhatsApp',
    email: 'Correo electrónico',
    service: '¿Qué necesitas?',
    budget: 'Presupuesto mensual aproximado',
    submit: 'Quiero mi propuesta',
    sending: 'Enviando…',
    error: 'No pudimos enviar tu información. Escríbenos por WhatsApp o intenta de nuevo.',
    required: 'Completa los campos requeridos.',
    thanksTitle: '¡Recibido!',
    thanksBody: 'Te escribimos por WhatsApp en menos de 24 horas. Si quieres avanzar más rápido:',
    whatsapp: 'Escríbenos por WhatsApp',
    book: 'Reservar una consulta',
    privacy: 'Usamos tus datos solo para responderte sobre tu propuesta.',
    consent: 'Acepto que Mikel Media me contacte por WhatsApp y correo sobre mi solicitud.',
    newsletter: 'Quiero recibir consejos y novedades de Mikel Media por email. Puedo darme de baja cuando quiera.',
    services: [
      ['todo', 'Todo: anuncios, contenido, web y automatización'],
      ['anuncios', 'Anuncios (Meta / Google)'],
      ['contenido', 'Contenido y redes sociales'],
      ['web', 'Web, SEO e IA'],
      ['automatizacion', 'Automatización y proyectos'],
      ['no-se', 'No sé todavía, necesito orientación'],
    ],
    budgets: [
      ['menos-300', 'Menos de $300 al mes'],
      ['300-750', '$300 – $750 al mes'],
      ['750-1500', '$750 – $1,500 al mes'],
      ['1500-mas', 'Más de $1,500 al mes'],
      ['no-se', 'Aún no lo sé'],
    ],
  },
  en: {
    eyebrow: 'Digital marketing agency · Puerto Rico',
    title: 'Ads. Content. Web. Automation.',
    titleAccent: 'One team.',
    sub: 'Tell us about your business and we will reply on WhatsApp within 24 hours with a clear proposal.',
    bullets: ['Meta & Google ads', 'Content & social media', 'Web, SEO & AI visibility', 'Automation & project management'],
    name: 'Your name',
    business: 'Business name',
    phone: 'WhatsApp',
    email: 'Email',
    service: 'What do you need?',
    budget: 'Approximate monthly budget',
    submit: 'Get my proposal',
    sending: 'Sending…',
    error: 'We could not send your details. Message us on WhatsApp or try again.',
    required: 'Please complete the required fields.',
    thanksTitle: 'Received!',
    thanksBody: 'We will message you on WhatsApp within 24 hours. To move faster:',
    whatsapp: 'Message us on WhatsApp',
    book: 'Book a consultation',
    privacy: 'We only use your details to reply about your proposal.',
    consent: 'I agree that Mikel Media may contact me by WhatsApp and email about my request.',
    newsletter: 'I want to receive tips and news from Mikel Media by email. I can unsubscribe anytime.',
    services: [
      ['todo', 'Everything: ads, content, web and automation'],
      ['anuncios', 'Ads (Meta / Google)'],
      ['contenido', 'Content & social media'],
      ['web', 'Web, SEO & AI'],
      ['automatizacion', 'Automation & projects'],
      ['no-se', 'Not sure yet, I need guidance'],
    ],
    budgets: [
      ['menos-300', 'Under $300 / month'],
      ['300-750', '$300 – $750 / month'],
      ['750-1500', '$750 – $1,500 / month'],
      ['1500-mas', 'Over $1,500 / month'],
      ['no-se', 'Not sure yet'],
    ],
  },
};

const INITIAL = { name: '', business: '', phone: '', email: '', service: 'todo', budget: 'no-se', website: '', consent: false, newsletter: false };

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const readUtm = () => {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(UTM_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
};

const postJson = (url, body) =>
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res;
  });

const trackLead = (form) => {
  try {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'empezar', content_category: form.service });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'generate_lead', lead_source: 'empezar', lead_service: form.service, lead_budget: form.budget });
  } catch (e) {
    // Tracking must never block the lead.
  }
};

const EmpezarPage = () => {
  const { i18n } = useTranslation();
  const t = COPY[i18n.language?.startsWith('en') ? 'en' : 'es'];
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error | invalid
  const submitted = useRef(false);
  const openedAt = useRef(Date.now());

  useEffect(() => {
    document.title = 'Mikel Media — Anuncios, Contenido, Web y Automatización';
  }, []);

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const toggleConsent = (e) => setForm((prev) => ({ ...prev, consent: e.target.checked }));
  const toggleNewsletter = (e) => setForm((prev) => ({ ...prev, newsletter: e.target.checked }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitted.current || status === 'sending') return;
    if (form.website) return; // honeypot
    if (!form.name.trim() || !form.business.trim() || !form.phone.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');
    const serviceLabel = t.services.find(([v]) => v === form.service)?.[1] || form.service;
    const budgetLabel = t.budgets.find(([v]) => v === form.budget)?.[1] || form.budget;
    const message = [
      'Lead desde anuncios (/empezar)',
      `Negocio: ${form.business.trim()}`,
      `Servicio: ${serviceLabel}`,
      `Presupuesto: ${budgetLabel}`,
      `Origen: ${window.location.search || 'directo'}`,
    ].join('\n');
    const lang = i18n.language?.startsWith('en') ? 'en' : 'es';
    // Two independent writes: the site database (admin panel) and the lead pipeline
    // (CRM + alert to Mikel + automatic replies). The lead counts as received if either lands.
    const results = await Promise.allSettled([
      postJson(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message,
        service: form.service,
        language: lang,
      }),
      postJson(LEAD_API, {
        name: form.name,
        business: form.business,
        email: form.email,
        phone: form.phone,
        service: form.service,
        budget: form.budget,
        lang,
        consent: form.consent,
        newsletter: form.newsletter,
        website: form.website,
        elapsedMs: Date.now() - openedAt.current,
        utm: readUtm(),
      }),
    ]);
    if (results.every((r) => r.status === 'rejected')) {
      setStatus('error');
      return;
    }
    submitted.current = true;
    trackLead(form);
    setStatus('done');
  };

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hola, soy ${form.name || ''} de ${form.business || 'mi negocio'}. Acabo de llenar el formulario en mikel.media.`
  )}`;

  const inputCls =
    'w-full bg-white/5 border border-white/15 text-white placeholder-white/40 px-4 py-3.5 font-source-sans text-base focus:outline-none focus:border-[#4EBFBF] transition-colors';
  const labelCls = 'block text-xs font-semibold tracking-widest uppercase text-white/60 font-poppins mb-2';

  return (
    <div data-testid="empezar-page" className="min-h-screen bg-[#0F1C2E] text-white relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#7D468C]/20 pointer-events-none" style={{ transform: 'rotate(45deg)' }} />
      <div className="absolute bottom-0 -left-16 w-64 h-64 border-2 border-[#4EBFBF]/15 pointer-events-none" style={{ transform: 'rotate(45deg)' }} />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <a href="/" aria-label="Mikel Media">
          <img src="/logo-onnavy.svg" alt="Mikel Media" className="h-14 sm:h-16 w-auto" />
        </a>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-10 sm:mt-14 items-start">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-3 h-3 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
              <span className="text-xs sm:text-sm font-semibold text-[#4EBFBF] tracking-widest uppercase font-poppins">{t.eyebrow}</span>
            </div>
            <h1 className="font-poppins font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.title}
              <span className="block mt-2 text-[#4EBFBF]">{t.titleAccent}</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 font-source-sans leading-relaxed max-w-xl">{t.sub}</p>
            <ul className="mt-8 space-y-3">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-center gap-3 font-source-sans text-white/85">
                  <CheckCircle size={18} className="text-[#4EBFBF] flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white/5 border border-white/10 p-6 sm:p-8">
            {status === 'done' ? (
              <div data-testid="empezar-thanks" className="py-6 text-center">
                <div className="w-14 h-14 mx-auto mb-5 bg-[#4EBFBF]/20 flex items-center justify-center" style={{ transform: 'rotate(45deg)' }}>
                  <CheckCircle size={26} className="text-[#4EBFBF]" style={{ transform: 'rotate(-45deg)' }} />
                </div>
                <h2 className="font-poppins font-black text-3xl">{t.thanksTitle}</h2>
                <p className="mt-3 text-white/70 font-source-sans">{t.thanksBody}</p>
                <div className="mt-7 flex flex-col gap-3">
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#4EBFBF] text-[#0F1C2E] font-poppins font-bold text-sm tracking-wide hover:opacity-90 transition-opacity">
                    <MessageCircle size={18} /> {t.whatsapp}
                  </a>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-white/25 text-white font-poppins font-semibold text-sm tracking-wide hover:border-white/50 transition-colors">
                    {t.book} <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="empezar-form" className="space-y-5">
                <div>
                  <label htmlFor="emp-name" className={labelCls}>{t.name} *</label>
                  <input id="emp-name" className={inputCls} value={form.name} onChange={update('name')} autoComplete="name" required />
                </div>
                <div>
                  <label htmlFor="emp-business" className={labelCls}>{t.business} *</label>
                  <input id="emp-business" className={inputCls} value={form.business} onChange={update('business')} autoComplete="organization" required />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="emp-phone" className={labelCls}>{t.phone} *</label>
                    <input id="emp-phone" type="tel" inputMode="tel" className={inputCls} value={form.phone} onChange={update('phone')} autoComplete="tel" placeholder="+1 787 000 0000" required />
                  </div>
                  <div>
                    <label htmlFor="emp-email" className={labelCls}>{t.email} *</label>
                    <input id="emp-email" type="email" className={inputCls} value={form.email} onChange={update('email')} autoComplete="email" required />
                  </div>
                </div>
                <div>
                  <label htmlFor="emp-service" className={labelCls}>{t.service}</label>
                  <select id="emp-service" className={inputCls} value={form.service} onChange={update('service')}>
                    {t.services.map(([v, l]) => (
                      <option key={v} value={v} className="text-[#0F1C2E]">{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="emp-budget" className={labelCls}>{t.budget}</label>
                  <select id="emp-budget" className={inputCls} value={form.budget} onChange={update('budget')}>
                    {t.budgets.map(([v, l]) => (
                      <option key={v} value={v} className="text-[#0F1C2E]">{l}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={toggleConsent} className="mt-1 h-4 w-4 accent-[#4EBFBF]" />
                  <span className="text-sm text-white/70 font-source-sans leading-snug">{t.consent}</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.newsletter} onChange={toggleNewsletter} className="mt-1 h-4 w-4 accent-[#4EBFBF]" />
                  <span className="text-sm text-white/70 font-source-sans leading-snug">{t.newsletter}</span>
                </label>
                {/* Honeypot: hidden from people, bots fill it. */}
                <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={update('website')} style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

                {status === 'invalid' && <p role="alert" className="text-[#F2785C] text-sm font-source-sans">{t.required}</p>}
                {status === 'error' && <p role="alert" className="text-[#F2785C] text-sm font-source-sans">{t.error}</p>}

                <button type="submit" disabled={status === 'sending'} className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#F2785C] text-[#0F1C2E] font-poppins font-bold text-base tracking-wide hover:opacity-90 disabled:opacity-60 transition-opacity">
                  {status === 'sending' ? t.sending : t.submit}
                  {status !== 'sending' && <ArrowRight size={18} />}
                </button>
                <p className="text-xs text-white/45 font-source-sans text-center">{t.privacy}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmpezarPage;
